import { createSlice } from "@reduxjs/toolkit";
import { 
  fetchFavorites, 
  submitNewFavorite, 
  removeFavoriteFromAPI,
  clearAllFavorites,
} from "../../services/favoriteThunk";

const initialState = {
  items: [],
  loading: false,
  error: null,
  lastFetched: null,
  isInitialized: false,
  clearingAll: false,
};

const favoriteSlice = createSlice({
  name: "favorite",
  initialState,
  reducers: {
    addToFavorites(state, action) {
      const item = action.payload;
      
      const exists = state.items.find(i =>
        i._id === item._id &&
        (item.remixId ? i.remixId === item.remixId : !i.remixId)
      );
      if (!exists) {
        const favoriteItem = {
          ...item,
          addedAt: new Date().toISOString(),
        };

        if (item.remixId && item.editInfos && item.editInfos.length > 0) {
          const favoritedRemixIndex = item.editInfos.findIndex(
            edit => edit._id === item.remixId || edit.id === item.remixId
          );
          
          if (favoritedRemixIndex > 0) {
            const favoritedRemix = item.editInfos[favoritedRemixIndex];
            favoriteItem.editInfos = [
              favoritedRemix,
              ...item.editInfos.slice(0, favoritedRemixIndex),
              ...item.editInfos.slice(favoritedRemixIndex + 1)
            ];
          }
        }

        state.items.unshift(favoriteItem);
      }
    },
    
    removeFromFavorites(state, action) {
      const { trackId, remixId } = action.payload;
      state.items = state.items.filter(item =>
        !(item._id === trackId &&
        (remixId ? item.remixId === remixId : !item.remixId))
      );
    },
    
    clearFavorites(state) {
      state.items = [];
    },
    
    resetFavorites(state) {
      return { ...initialState };
    },
    
    setFavorites(state, action) {
      state.items = action.payload;
      state.lastFetched = new Date().toISOString();
      state.isInitialized = true;
    },
    
    clearError(state) {
      state.error = null;
    },
  },
  
  extraReducers: (builder) => {
    builder
      .addCase(fetchFavorites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.lastFetched = new Date().toISOString();
        state.isInitialized = true;
        
        
        if (action.payload.data?.tracks) {
          console.log("Fetched favorites:", action.payload.data.tracks);
          state.items = action.payload.data.tracks.map(track => ({
            _id: track.trackId,
            trackId: track.trackId, 
            trackTitle: track.trackTitle,
            artists: track.artists,
            coverArt: track.coverArt,
            danceability: track.danceability,
            energy: track.energy,
            valence: track.valence,
            popularity: track.popularity,
            totalDownloads: track.totalDownloads,
            remixId: track.editInfos?.[0]?.id || track.editInfos?.[0]?._id, 
            editInfos: track.editInfos || [],
            favoriteCreatedAt: track.favoriteCreatedAt,
            ...track
          }));
          console.log("Processed favorites:", state.items);
        } else {
          state.items = [];
        }
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch favorites";
        state.isInitialized = true;
      })
      
      .addCase(submitNewFavorite.pending, (state) => {
        state.error = null;
      })
      .addCase(submitNewFavorite.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(submitNewFavorite.rejected, (state, action) => {
        state.error = action.error.message || "Failed to add favorite";
        
        const { trackId, remixId } = action.meta.arg;
        state.items = state.items.filter(item =>
          !(item._id === trackId &&
          (remixId ? item.remixId === remixId : !item.remixId))
        );
      })
      
      .addCase(removeFavoriteFromAPI.pending, (state) => {
        state.error = null;
      })
      .addCase(removeFavoriteFromAPI.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(removeFavoriteFromAPI.rejected, (state, action) => {
        state.error = action.error.message || "Failed to remove favorite";
        
        const { trackId, remixId, trackMetadata } = action.meta.arg;
        if (trackMetadata) {
          const exists = state.items.find(i =>
            i._id === trackId &&
            (remixId ? i.remixId === remixId : !i.remixId)
          );
          
          if (!exists) {
            const restoredItem = {
              ...trackMetadata,
              _id: trackId,
              trackId,
              remixId,
              addedAt: new Date().toISOString(),
            };

            if (remixId && trackMetadata.editInfos && trackMetadata.editInfos.length > 0) {
              const favoritedRemixIndex = trackMetadata.editInfos.findIndex(
                edit => edit._id === remixId || edit.id === remixId
              );
              
              if (favoritedRemixIndex > 0) {
                const favoritedRemix = trackMetadata.editInfos[favoritedRemixIndex];
                restoredItem.editInfos = [
                  favoritedRemix,
                  ...trackMetadata.editInfos.slice(0, favoritedRemixIndex),
                  ...trackMetadata.editInfos.slice(favoritedRemixIndex + 1)
                ];
              }
            }

            state.items.unshift(restoredItem);
          }
        }
      })
      
      .addCase(clearAllFavorites.pending, (state) => {
        state.clearingAll = true;
        state.error = null;
      })
      .addCase(clearAllFavorites.fulfilled, (state, action) => {
        state.clearingAll = false;
        state.error = null;
        state.items = [];
        console.log("All favorites cleared successfully");
      })
      .addCase(clearAllFavorites.rejected, (state, action) => {
        state.clearingAll = false;
        state.error = action.payload || "Failed to clear all favorites";
        console.error("Failed to clear all favorites:", action.payload);
      })
      
      .addCase('auth/logout', (state) => {
        return { ...initialState };
      })
      .addCase('auth/handleSessionExpiration', (state) => {
        return { ...initialState };
      });
  }
});

export const {
  addToFavorites,
  removeFromFavorites,
  clearFavorites,
  resetFavorites, 
  setFavorites,
  clearError
} = favoriteSlice.actions;

export default favoriteSlice.reducer;